import React from 'react';
import Sparkline from './Sparkline';
import { Trend } from './CompaniesTab';
import { formatDate, formatNumber, weekNumber } from './engagementHelpers';

/*
 * Tab "Korjaamot" (OVIO-105, Pencil frames 00 and 03): Ovio-button presses of ERP-connected workshops
 * with a risk status, or, when a start week is known, the pilot-success panel and its criteria table.
 */
const BOLD = 'TitilliumWeb-SemiBold';
const num = { textAlign: 'right', whiteSpace: 'nowrap' };
const th = {
  fontSize: 13, fontWeight: 'normal', color: '#4A5560', padding: '10px 5px', borderBottom: '1px solid #E3E6EA',
  textAlign: 'left', verticalAlign: 'bottom', lineHeight: '16px',
};
const td = { fontSize: 15, color: '#222', padding: '0 5px', height: 50, borderBottom: '1px solid #E3E6EA' };
export const STATUSES = ['never', 'silent', 'declining', 'active'];
const CHIP = {
  never: { background: '#ECEEF0', color: '#3D4650' },
  silent: { background: '#FDE2E2', color: '#B71C1C' },
  declining: { background: '#FFF1C7', color: '#8A5300' },
  active: { background: '#D9F5E1', color: '#1E6B34' },
};

export function StatusChip({ status, t }) {
  return (
    <span style={{
      ...CHIP[status], borderRadius: 14, padding: '3px 10px', fontSize: 14, fontFamily: BOLD, whiteSpace: 'nowrap',
    }}
    >
      {t('status_' + status)}
    </span>
  );
}

function StatusCounts({ data, t }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', margin: '24px 0 16px', fontSize: 14, color: '#4A5560',
    }}
    >
      {STATUSES.map(status => (
        <span key={status} data-status-count style={{ marginRight: 22, display: 'inline-flex', alignItems: 'center' }}>
          <StatusChip status={status} t={t} />
          <span style={{ fontSize: 20, fontFamily: BOLD, color: '#222', marginLeft: 8 }}>{data.counts[status]}</span>
        </span>
      ))}
      <span>{t('populationNote', { count: data.population })}</span>
    </div>
  );
}

class WorkshopList extends React.Component {
  constructor(props) {
    super(props);
    this.state = { reversed: false };
  }

  render() {
    const { data, t } = this.props;
    const { reversed } = this.state;
    const rows = reversed ? data.rows.slice().reverse() : data.rows;
    return (
      <div>
        <StatusCounts data={data} t={t} />
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>{t('colWorkshop')}</th>
                <th style={th}>{t('colChain')}</th>
                <th style={th} data-sort-status aria-sort={reversed ? 'descending' : 'ascending'}>
                  <button
                    type='button'
                    onClick={() => this.setState({ reversed: !reversed })}
                    style={{
                      border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer',
                    }}
                  >
                    {t('colStatus')}
                    {reversed ? ' ↑' : ' ↓'}
                  </button>
                </th>
                <th style={th}>{t('colPresses26')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('colPresses4')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('colOpened')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('colCars4')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('colTrend')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td style={{ ...td, color: '#5F6B76' }} colSpan={8}>{t('empty')}</td></tr>
              )}
              {rows.map(row => (
                <tr key={row.id}>
                  <td style={td}>
                    {row.name}
                    {row.disabled && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: '#7A848D' }}>{t('disabled')}</span>
                    )}
                  </td>
                  <td style={{ ...td, color: row.chains ? '#222' : '#9AA3AB' }}>{row.chains || '–'}</td>
                  <td style={td}><StatusChip status={row.status} t={t} /></td>
                  <td style={td}>
                    <Sparkline values={row.series} weeks={data.weeks} label={row.name + ': ' + t('colPresses26')} />
                  </td>
                  <td style={{ ...td, ...num }}>{formatNumber(row.presses_4w)}</td>
                  <td style={{ ...td, ...num }}>
                    {row.presses_4w && row.open_share_4w !== null ? row.opened_4w + '/' + row.presses_4w : '–'}
                  </td>
                  <td style={{ ...td, ...num }}>{formatNumber(row.cars_4w)}</td>
                  <td style={{ ...td, ...num }}><Trend value={row.trend} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 13, color: '#5F6B76', lineHeight: '20px', marginTop: 24 }}>
          {t('helpWorkshops', { week: weekNumber(data.current_week) })}
        </p>
      </div>
    );
  }
}

export default function WorkshopsTab({ data, t, onStartWeek }) {
  if (data.pilot) {
    // eslint-disable-next-line no-use-before-define
    return <PilotPanel data={data} pilot={data.pilot} t={t} onStartWeek={onStartWeek} />;
  }
  return <WorkshopList data={data} t={t} />;
}

const yearWeek = label => weekNumber(label) + '/' + label.split('-W')[0];
const mark = ok => <span style={{ color: ok ? '#1E7B34' : '#C62828', fontSize: 18 }}>{ok ? '✓' : '✗'}</span>;

function PilotPanel({
  data, pilot, t, onStartWeek,
}) {
  const complete = data.weeks.slice(0, -1);
  const choices = complete.indexOf(pilot.start_week) === -1 ? [pilot.start_week].concat(complete) : complete;
  let stateText = t('pilotDone');
  if (pilot.state === 'partial') {
    stateText = t('pilotPartial', { k: pilot.k });
  }
  let figure = null;
  let explain = null;
  if (pilot.state === 'not_assessable') {
    explain = t('pilotNotAssessable', { date: pilot.data_start ? formatDate(pilot.data_start) : '–' });
  } else if (pilot.state === 'empty') {
    explain = t('pilotEmpty');
  } else {
    figure = (
      <div style={{ minWidth: 150, marginRight: 30 }}>
        <div style={{ fontSize: 44, fontFamily: BOLD, color: '#0077B3', lineHeight: '52px' }}>
          {(pilot.state === 'partial' ? '–' : pilot.numerator) + ' / ' + pilot.denominator}
        </div>
        <div style={{ fontSize: 17, fontFamily: BOLD, marginTop: 6 }}>
          {pilot.state === 'partial'
            ? stateText
            : t('pilotShare', { percent: Math.round((100 * pilot.numerator) / pilot.denominator) })}
        </div>
      </div>
    );
    explain = t('pilotExplain', { denominator: pilot.denominator, size: pilot.group_size, end: weekNumber(pilot.end_week) });
  }
  const result = (row) => {
    if (pilot.state === 'partial') {  // no verdict before the sixth week is complete
      return <span style={{ color: '#5F6B76' }}>{t('pilotPending')}</span>;
    }
    if (row.passed) {
      return <span style={{ color: '#1E7B34', fontFamily: BOLD }}>{t('pilotPassed')}</span>;
    }
    return <span style={{ color: '#C62828', fontFamily: BOLD }}>{t('pilotFailed')}</span>;
  };
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', background: '#EEF6FC', borderRadius: 4, padding: '22px 24px', marginTop: 24,
      }}
      >
        {figure}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontFamily: BOLD, color: '#222' }}>
            {t('pilotHeading', {
              start: yearWeek(pilot.start_week), from: weekNumber(pilot.start_week), to: weekNumber(pilot.end_week), state: stateText,
            })}
          </div>
          <label style={{ fontSize: 13, color: '#4A5560', display: 'block', margin: '8px 0' }}>
            {t('pilotStartWeek')}
            <select
              name='start_week'
              value={pilot.start_week}
              onChange={event => onStartWeek(event.target.value)}
              style={{ marginLeft: 8, fontFamily: 'inherit', fontSize: 14 }}
            >
              {choices.map(week => <option key={week} value={week}>{yearWeek(week)}</option>)}
            </select>
          </label>
          <div style={{ fontSize: 14, color: '#4A5560', lineHeight: '22px' }}>{explain}</div>
        </div>
      </div>
      {pilot.rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20 }}>
            <thead>
              <tr>
                <th style={th}>{t('colWorkshop')}</th>
                <th style={th}>{t('colBefore')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('colWeeksPressed')}</th>
                <th style={{ ...th, textAlign: 'center' }}>≥ 4/6</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('colOpenedShare')}</th>
                <th style={{ ...th, textAlign: 'center' }}>≥ 50 %</th>
                <th style={th}>{t('colStatusEnd', { week: weekNumber(pilot.status_week) })}</th>
                <th style={th}>{t('colResult')}</th>
              </tr>
            </thead>
            <tbody>
              {pilot.rows.map(row => (
                <tr key={row.id}>
                  <td style={td}>{row.name}</td>
                  <td style={td}><StatusChip status={row.before} t={t} /></td>
                  <td style={{ ...td, ...num }}>{row.weeks_pressed + '/6'}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{mark(row.weeks_ok)}</td>
                  <td style={{ ...td, ...num }}>{row.open_share === null ? '–' : row.open_share + ' %'}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{mark(row.open_ok)}</td>
                  <td style={td}>{row.status_end ? <StatusChip status={row.status_end} t={t} /> : '–'}</td>
                  <td style={td}>{result(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p style={{ fontSize: 13, color: '#5F6B76', lineHeight: '20px', marginTop: 24 }}>{t('pilotHelp')}</p>
    </div>
  );
}
